import './editParticipantsPopup.scss';

import { Guid } from '@approvalmax/types';
import { Popup, TransparentButton } from '@approvalmax/ui';
import { arrayHelpers, compareHelpers } from '@approvalmax/utils';
import uniqBy from 'lodash/uniqBy';
import { selectors, ui } from 'modules/common';
import { domain, State } from 'modules/data';
import { addArrayItem } from 'modules/immutable';
import { useShallowEqualSelector } from 'modules/react-redux';
import { DeleteIcon, LockIcon } from 'modules/sprites';
import { FC, memo, useState } from 'react';
import bemFactory from 'react-bem-factory';
import { useDispatch } from 'react-redux';

import { cancelActivePopup, editStepParticipants } from '../actions';
import { createStepParticipant } from '../factories';
import { EditParticipantsPopupData } from '../reducers/page/activePopup/editParticipantsPopupReducer';
import { getActivePopup, isActiveRequestLocked } from '../selectors/pageSelectors';
import { isStepParticipantLocked } from '../selectors/requestSelectors';
import { getMemberOrUser } from '../selectors/userSelectors';
import { StepParticipantHover } from './card/StepParticipantHover/StepParticipantHover';
import { messages } from './EditParticipantsPopup.messages';

const bem = bemFactory.block('reql-edit-participants-popup');
const qa = bemFactory.qa('reql-edit-participants-popup');

const EditParticipantsPopup: FC = () => {
    const dispatch = useDispatch();
    const { request, step, me, lockedParticipants, isLocked, team, allUsers } = useShallowEqualSelector(
        (state: State) => {
            const { requestId, stepId } = getActivePopup<EditParticipantsPopupData>(state);
            const me = selectors.profile.getProfileUser(state).id;
            const request = selectors.request.getRequestById(state, requestId);
            const step = request.steps.find((s) => s.id === stepId)!;
            const lockedParticipants = step.participants
                .filter((p) => isStepParticipantLocked(me, request, p))
                .map((p) => p.delegateFor || p.userId);

            return {
                request,
                step,
                me,
                lockedParticipants,
                isLocked: isActiveRequestLocked(state),
                team: selectors.company.getCompanyTeam(state, request.company),
                allUsers: selectors.user.getUsers(state),
            };
        }
    );
    const [commentText, setCommentText] = useState('');

    const getUser = (userId: Guid) => getMemberOrUser(userId, team, allUsers);

    const [participants, setParticipants] = useState(
        arrayHelpers.arraySort(
            uniqBy(step.participants, (p) => p.delegateFor || p.userId),
            (a, b) => {
                const ua = getUser(a.userId);
                const ub = getUser(b.userId);

                return compareHelpers.stringComparator2AscI(ua.displayName, ub.displayName);
            }
        )
    );

    const getPossibleParticipants = () => {
        const myRequest = request.authorId === me;

        return team.filter((user) => {
            const notInParticipants = !participants.some(
                (item) => user.id === item.userId || user.id === item.delegateFor
            );
            const meIsNotTheTeamMember = user.id !== me;

            return notInParticipants && (!myRequest || meIsNotTheTeamMember);
        });
    };

    const onAddApprover = (user: selectors.types.ExpandedCompanyUser) => {
        setParticipants(addArrayItem(participants, createStepParticipant(me, user.id)));
    };

    const onRemoveParticipant = (participant: domain.RequestStepParticipant) => {
        setParticipants((prevParticipants) =>
            prevParticipants.filter(
                (prevParticipant) =>
                    lockedParticipants.includes(prevParticipant.delegateFor || prevParticipant.userId) ||
                    prevParticipant.userId !== participant.userId
            )
        );
    };

    const onDone = () => {
        const addedEmails = participants
            .filter(({ userId }) => !step.participants.some((item) => item.userId === userId))
            .map((item) => getUser(item.delegateFor || item.userId).userEmail);

        const removedEmails = step.participants
            .map((item) => item.userId)
            .filter((userId) => !participants.map((item) => item.userId).some((id) => id === userId));

        if (addedEmails.length === 0 && removedEmails.length === 0) {
            dispatch(cancelActivePopup());

            return;
        }

        dispatch(
            editStepParticipants({
                requestId: request.id,
                step: step,
                addedEmails,
                removedEmails,
                commentText: commentText,
                companyId: request.companyId,
            })
        );
    };

    const participantsMap = participants.map((p) => {
        const delegateUser = p.delegateFor && getUser(p.delegateFor);
        const user = getUser(p.userId);

        const locked = lockedParticipants.includes(p.delegateFor || p.userId);

        return (
            <div
                key={(delegateUser || user).id}
                className={bem('participant-item', { locked })}
                data-qa={qa('participant-item')}
                data-qa-id={(delegateUser || user).id}
                data-qa-name={user.displayName}
            >
                <StepParticipantHover participant={p} className={bem('participant-hover-anchor')}>
                    <ui.Avatar title={null} size={30} user={user} className={bem('participant-avatar')} />

                    <div className={bem('participant-name')}>{user.displayName}</div>
                </StepParticipantHover>

                {locked ? (
                    <LockIcon className={bem('participant-lock-icon')} width={8} height={10} />
                ) : (
                    <TransparentButton
                        execute={() => onRemoveParticipant(p)}
                        className={bem('remove-participant-button')}
                        qa={qa('remove-participant-button')}
                    >
                        <DeleteIcon width={8} height={8} />
                    </TransparentButton>
                )}
            </div>
        );
    });

    const possibleParticipants = getPossibleParticipants();

    return (
        <Popup.DefaultContent
            className={bem.add('fs-mask')()}
            qa={qa()}
            title={messages.popupTitle}
            buttons={
                <Popup.DefaultContent.Button
                    qa={qa('done-button')}
                    disabled={isLocked}
                    execute={onDone}
                    title={messages.doneButtonTitle}
                >
                    {messages.doneButtonText}
                </Popup.DefaultContent.Button>
            }
        >
            <div className={bem('content')}>
                <div className={bem('description-block')}>
                    {messages.descriptionText({
                        authorName: request.author.displayName,
                        stepName: <b>{step.name}</b>,
                        br: <br />,
                    })}
                </div>

                <div className={bem('participant-list-block')} data-qa={qa('participant-list-block')}>
                    {participantsMap}

                    {possibleParticipants.length > 0 && (
                        <ui.UserPicker
                            qa={qa('user-picker')}
                            selectButtonText={messages.addApproverButtonText}
                            users={possibleParticipants}
                            value={null}
                            onChange={onAddApprover}
                            theme={ui.UserPicker.Theme.Small}
                        />
                    )}
                </div>

                <div className={bem('comment-editor-block')}>
                    <ui.AddCommentEditor qa={qa('comment-editor')} value={commentText} onChange={setCommentText} />
                </div>
            </div>
        </Popup.DefaultContent>
    );
};

export default memo(EditParticipantsPopup);
